// Состояние входа. Токен доступа живёт ТОЛЬКО в памяти: localStorage читается любым скриптом,
// а система считает зарплаты. Токен обновления — единственное, что переживает перезагрузку
// страницы, и лежит в хранилище браузера под именем ниже.

import { create } from 'zustand';

import type { UserPayload } from '@/shared/api/endpoints/auth';

/** Имя записи в localStorage (не секрет: секрет — её содержимое, оно в код не попадает). */
export const REFRESH_STORAGE_NAME = 'bark.auth.refresh';

/**
 * restoring — есть токен обновления, сессия поднимается; экраны ждут, не выкидывая на вход.
 * authenticated — вход подтверждён, user и permissions заполнены.
 * anonymous — сессии нет, защищённая область отправляет на /login.
 */
export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

interface AuthState {
  /** Токен доступа. Только память, при перезагрузке страницы исчезает — это осознанно. */
  accessToken: string | null;
  /** Момент истечения токена доступа, мс эпохи. Нужен проактивному обновлению. */
  accessTokenExpiresAt: number | null;
  user: UserPayload | null;
  /** Готовый список кодов прав с сервера. Клиент не выводит права из ролей сам. */
  permissions: readonly string[];
  /**
   * Контур демонстрационный: над разделами идёт полоса «демо». Приходит с сервера, а не из
   * сборки: одна и та же сборка ставится и на стенд, и на боевой, и различить их может
   * только тот, кто отвечает на запросы.
   */
  demoBanner: boolean;
  status: AuthStatus;

  setSession(session: {
    accessToken: string;
    expiresInSeconds: number;
    user: UserPayload;
    /** Не передан — контур обычный. Знает про это ровно один вызывающий, остальные (тесты
     *  разделов) про контур ничего не решают, и требовать от них поле значит просить их
     *  повторить одно и то же двадцать пять раз. */
    demoBanner?: boolean;
  }): void;
  setRestoring(): void;
  setAnonymous(): void;
}

export function readRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_STORAGE_NAME);
  } catch {
    // Хранилище может быть недоступно (приватный режим, урезанный WebView) — тогда
    // сессия просто не переживает перезагрузку, это деградация, а не поломка.
    return null;
  }
}

export function writeRefreshToken(value: string): void {
  try {
    window.localStorage.setItem(REFRESH_STORAGE_NAME, value);
  } catch {
    // См. readRefreshToken: без хранилища работаем в пределах одной вкладки.
  }
}

export function clearRefreshToken(): void {
  try {
    window.localStorage.removeItem(REFRESH_STORAGE_NAME);
  } catch {
    // Нечего чистить, если хранилища нет.
  }
}

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  user: null,
  permissions: [],
  demoBanner: false,
  // Старт со статусом по факту: есть токен обновления — поднимаемся, нет — сразу на вход.
  status: readRefreshToken() !== null ? 'restoring' : 'anonymous',

  setSession: ({ accessToken, expiresInSeconds, user, demoBanner = false }) =>
    set({
      accessToken,
      accessTokenExpiresAt: Date.now() + expiresInSeconds * 1000,
      user,
      permissions: user.permissions,
      demoBanner,
      status: 'authenticated',
    }),

  setRestoring: () => set({ status: 'restoring' }),

  setAnonymous: () =>
    set({
      accessToken: null,
      accessTokenExpiresAt: null,
      user: null,
      permissions: [],
      // Признак контура НЕ сбрасывается: контур от выхода человека не меняется, а полоса,
      // мигнувшая на экране входа, читалась бы как «демо кончилось».
      status: 'anonymous',
    }),
}));
